// A KEZDŐLAP MENTETT ELRENDEZÉSE: lekérdezés + a feloldás (`resolveLayout`) + mentés/visszaállítás (optimista).
//
// HIBATŰRÉS: ha a preferencia-GET hibázik (502 stb.), az ALAPÉRTELMEZETT elrendezés jelenik meg, a lap nem dől el, a
// „Testreszabás" pedig tiltott (`editable = false`) — mentett verzió nélkül nem tudnánk helyesen írni (`If-Match`).
//
// MENTÉS: a `useEntityMutation`-en át (`onMutate` cache-írás → rollback → `onSettled` invalidálás). A sikeres válasz a
// cache-be kerül (az új `version`), így egy azonnali újabb mentés nem fut 409-re. A jog nélküli, de ismert id-ket a
// `mergeHidden` fűzi vissza (jog-visszaadáskor visszajön a csempe a helyére).
import {
  DASHBOARD_CATALOG_REV,
  mergeHidden,
  resolveLayout,
  type DashboardLayout,
  type DashboardTileEntry,
  type ResolvedLayout,
} from '@atlas/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  dashboardKeys,
  isEnvelope,
  withEnvelopeValue,
  type CachedResponse,
} from '@/features/dashboard/api';
import { api } from '@/lib/api';
import { useEntityMutation, type EntityMutationResult } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

interface SaveVars {
  entries: DashboardTileEntry[];
  version: number;
  saved: DashboardLayout | null;
}

export interface DashboardLayoutHandle {
  layout: ResolvedLayout;
  /** A mentett (nyers) elrendezés — `null`: sosem szabott. */
  saved: DashboardLayout | null;
  isLoading: boolean;
  /** Szerkeszthető-e (volt sikeres GET → ismert a `version`). */
  editable: boolean;
  /** A látható csempék mentése (a rejtettek visszafűzésével). `onError` a kérés hibájára (a hívó dönt). */
  save: (entries: DashboardTileEntry[], callbacks?: { onError?: (err: unknown) => void }) => void;
  reset: (callbacks?: { onError?: (err: unknown) => void }) => void;
  isSaving: boolean;
}

export function useDashboardLayout(): DashboardLayoutHandle {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const query = api.preferences.dashboardGet.useQuery(
    dashboardKeys.layout,
    {},
    {
      queryKey: dashboardKeys.layout,
    },
  );
  const envelope = query.data?.status === 200 ? query.data.body : null;
  const saved: DashboardLayout | null = envelope?.value ?? null;
  const version = envelope?.version ?? 0;

  const layout = useMemo(() => resolveLayout(saved, can), [saved, can]);

  const messages = {
    success: t('toast:dashboard.saved'),
    conflict: t('toast:dashboard.conflict'),
    forbidden: t('toast:dashboard.forbidden'),
    validation: t('toast:dashboard.validation'),
    error: t('toast:dashboard.error'),
    unavailable: t('toast:dashboard.unavailable'),
  };

  /** A sikeres válasz a cache-be (új `version`) — a következő mentés ezt használja `If-Match`-nek. */
  const writeBack = useCallback(
    (res: EntityMutationResult & { headers?: Headers }, value: DashboardLayout | null) => {
      if (res.status === 200 && isEnvelope(res.body)) {
        queryClient.setQueryData<CachedResponse>(dashboardKeys.layout, {
          status: 200,
          body: res.body,
          headers: res.headers ?? new Headers(),
        });
      } else if (res.status === 204) {
        queryClient.setQueryData<CachedResponse>(dashboardKeys.layout, {
          status: 200,
          body: { value, version: 0, updatedAt: null },
          headers: new Headers(),
        });
      }
    },
    [queryClient],
  );

  const saveMutation = useEntityMutation<SaveVars, CachedResponse>({
    mutationFn: async ({ entries, version: v, saved: current }) => {
      const body: DashboardLayout = {
        v: 1,
        catalogRev: DASHBOARD_CATALOG_REV,
        tiles: mergeHidden(current, entries, can),
      };
      const res = await api.preferences.dashboardPut.mutation({
        body,
        headers: { 'if-match': String(v) },
      });
      writeBack(res, null);
      return res;
    },
    invalidateKeys: [dashboardKeys.layout],
    optimistic: {
      queryKey: dashboardKeys.layout,
      update: (old, { entries, saved: current }) =>
        withEnvelopeValue(old, {
          v: 1,
          catalogRev: DASHBOARD_CATALOG_REV,
          tiles: mergeHidden(current, entries, can),
        } satisfies DashboardLayout),
    },
    messages,
  });

  const resetMutation = useEntityMutation<{ version: number }, CachedResponse>({
    mutationFn: async ({ version: v }) => {
      const res = await api.preferences.dashboardReset.mutation({
        headers: { 'if-match': String(v) },
      });
      writeBack(res, null);
      return res;
    },
    invalidateKeys: [dashboardKeys.layout],
    optimistic: { queryKey: dashboardKeys.layout, update: (old) => withEnvelopeValue(old, null) },
    messages: { ...messages, success: t('toast:dashboard.reset') },
  });

  return {
    layout,
    saved,
    isLoading: query.isLoading,
    editable: envelope !== null,
    isSaving: saveMutation.isPending || resetMutation.isPending,
    save: (entries, callbacks) => saveMutation.mutate({ entries, version, saved }, callbacks),
    reset: (callbacks) => resetMutation.mutate({ version }, callbacks),
  };
}
